import { useState, useEffect, useCallback, useRef } from 'react';
import { signalRService } from '../../services/signalRService';

export type OrchestratorEventType =
  | 'thinking'
  | 'token'
  | 'tool_call'
  | 'response_complete'
  | 'sub_agent_started'
  | 'sub_agent_token'
  | 'sub_agent_tool_call'
  | 'sub_agent_completed';

export interface OrchestratorEvent {
  id: string;
  type: OrchestratorEventType;
  timestamp: number;
  /** For sub-agent events, the callId that groups them. */
  callId?: string;
  /** For nested sub-agent tool calls, the parent callId. */
  parentCallId?: string;
  data: Record<string, unknown>;
}

/**
 * Subscribes to all orchestrator SignalR events for a session and reduces them
 * into a flat array with parent-pointer linking for tree rendering.
 */
export function useOrchestratorStream(sessionId: string | null) {
  const [events, setEvents] = useState<OrchestratorEvent[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const idCounter = useRef(0);

  const reset = useCallback(() => {
    setEvents([]);
    setStreamingText('');
    setIsRunning(false);
    idCounter.current = 0;
  }, []);

  useEffect(() => {
    if (!sessionId) return;

    const push = (evt: Omit<OrchestratorEvent, 'id' | 'timestamp'>) => {
      setEvents((prev) => [...prev, { ...evt, id: `evt_${++idCounter.current}`, timestamp: Date.now() }]);
    };

    signalRService.on({
      onAgentThinking: (d) => {
        if (d.sessionId !== sessionId) return;
        setIsRunning(true);
        push({ type: 'thinking', data: {} });
      },
      onTokenReceived: (d) => {
        if (d.sessionId !== sessionId) return;
        setStreamingText((prev) => prev + d.token);
        push({ type: 'token', data: { token: d.token } });
      },
      onToolCall: (d) => {
        if (d.sessionId !== sessionId) return;
        push({ type: 'tool_call', data: { toolName: d.toolName, arguments: d.arguments } });
      },
      onResponseComplete: (d) => {
        if (d.sessionId !== sessionId) return;
        setIsRunning(false);
        push({ type: 'response_complete', data: { response: d.response } });
      },
      onSubAgentStarted: (d) => {
        if (d.sessionId !== sessionId) return;
        push({
          type: 'sub_agent_started',
          callId: d.callId,
          data: { alias: d.alias, depth: d.depth, task: d.task },
        });
      },
      onSubAgentTokenReceived: (d) => {
        if (d.sessionId !== sessionId) return;
        push({ type: 'sub_agent_token', callId: d.callId, data: { token: d.token } });
      },
      onSubAgentToolCall: (d) => {
        if (d.sessionId !== sessionId) return;
        push({
          type: 'sub_agent_tool_call',
          callId: d.callId,
          data: { toolName: d.toolName, arguments: d.arguments },
        });
      },
      onSubAgentCompleted: (d) => {
        if (d.sessionId !== sessionId) return;
        push({
          type: 'sub_agent_completed',
          callId: d.callId,
          data: { result: d.result, success: d.success, errorMessage: d.errorMessage },
        });
      },
    });

    signalRService.joinSession(sessionId);

    return () => {
      signalRService.leaveSession(sessionId);
      signalRService.off();
    };
  }, [sessionId]);

  return { events, isRunning, streamingText, reset };
}

/**
 * Groups flat events into a tree keyed by callId.
 */
export function buildEventTree(events: OrchestratorEvent[]) {
  const topLevel: OrchestratorEvent[] = [];
  const subAgentEvents = new Map<string, OrchestratorEvent[]>();

  for (const evt of events) {
    if (evt.callId) {
      const list = subAgentEvents.get(evt.callId) ?? [];
      list.push(evt);
      subAgentEvents.set(evt.callId, list);
    } else {
      topLevel.push(evt);
    }
  }

  return { topLevel, subAgentEvents };
}
